/**
 * DataSourceSection Component (Story 5.34, Task 1)
 *
 * Data source configuration section for data-connected widget types.
 * Shows integration dropdown, metric selector, and dimension selector
 * populated dynamically from connected integrations and their schemas.
 *
 * Non-data widgets (text-block, heading, paragraph, rich-text, spacer-divider,
 * image, embed, cover-page) do NOT render this section.
 */

import { useCallback, useRef, useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Switch } from "@provenleap/ui";
import { SearchableSelect, type SearchableSelectGroup } from "./SearchableSelect";
import type { WidgetConfig } from "../../lib/templateUtils";
import { METRIC_PLATFORM_MAP } from "../../lib/templateUtils";
import { useClientIntegrations } from "../../hooks/useClientIntegrations";
import { useIntegrationSchema } from "../../hooks/useIntegrationSchema";
import { useClientMarkups } from "../../hooks/useClientMarkups";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
// Story 17.4 AC #10 — agency-eyes-only markup indicator (pure decision algebra
// in lib/markupIndicator.ts; consumes the shared @provenleap/integrations
// classifier so it never recomputes which metrics markup affects).
import { computeMarkupIndicator } from "../../lib/markupIndicator";
import { X, CaretDown, CaretUp, Percent } from "@phosphor-icons/react";
import { useToast } from "../../hooks/useToast";
import { Toast } from "../clients/Toast";
import { MetricSelector } from "./MetricSelector";
import { DimensionSelector } from "./DimensionSelector";
// Story 32.6: pure selection algebra for the per-widget account override
// picker (leaf lib module — no React/fetch; see widgetAccountSelection.ts).
import {
  deriveInvolvedPlatforms,
  groupAttachments,
  countAttachments,
  hasMultiAttachmentPlatform,
  normalizeStoredIntegrationIds,
  countSelectedExisting,
  deriveCheckedIds,
  computeSelectionPatch,
  isLastCheckedInGroup,
  reconcileOnMetricAdd,
  isCrossToolResolveSentinel,
} from "../../lib/widgetAccountSelection";
// Story 37.7: relocated to a leaf lib module so `lib/metricCompatibility.ts`
// can consume it without a lib→component import. Re-exported below for
// backward compatibility with existing `from "./DataSourceSection"` importers.
import { WIDGET_METRIC_COMPATIBILITY } from "../../lib/widgetCompatibility";

// ============================================================================
// Constants
// ============================================================================

/** Widget types that show the data source configuration section */
export const DATA_CONNECTED_WIDGET_TYPES: string[] = [
  "kpi",
  "line-chart",
  "bar-chart",
  "pie-chart",
  "data-table",
  "gauge",
  "funnel",
  "goal",
  "benchmark",
  "custom-metric",
  "top-posts",
  "demographic",
];

/** Re-exported from the leaf `widgetCompatibility` module (Story 37.7) — every
 *  existing `from "./DataSourceSection"` importer keeps working unchanged. */
export { WIDGET_METRIC_COMPATIBILITY };

/** Widget types that support dimension grouping (Group By) */
const DIMENSION_WIDGET_TYPES = new Set(["bar-chart", "pie-chart", "data-table"]);

/** Widget types that support multiple metrics */
export const MULTI_METRIC_WIDGET_TYPES = new Set(["line-chart", "data-table"]);

// ============================================================================
// Short platform labels for badges
// ============================================================================

const PLATFORM_SHORT_LABELS: Record<string, string> = {
  "google-analytics-4": "GA4",
  "google-search-console": "GSC",
  "google-ads": "GAds",
  "youtube-analytics": "YT",
  "google-business-profile": "GBP",
  "google-pagespeed": "PS",
  "facebook-pages": "FB",
  "facebook-ads": "FBAds",
  "instagram-business": "IG",
  "instagram-ads": "IGAds",
  "linkedin-pages": "LI",
  "linkedin-ads": "LIAds",
  "tiktok-business": "TT",
  mailchimp: "MC",
  shopify: "Shop",
};

/** The selected-metric shape this panel renders + edits. */
type SelectedMetric = { key: string; integrationId: string; label: string };

/** Single-metric widget types store their binding under `config.metricKey`. */
const SINGLE_METRIC_KEY_TYPES = new Set(["kpi", "pie-chart"]);
/** data-table renders from `config.columns` (string[]), not `config.metrics`. */
const COLUMNS_RENDERER_TYPE = "data-table";

/**
 * Story 37.7 review R3-1/2/3/4: build the config patch that writes a metric
 * selection in the EXACT shape each widget type's RENDERER reads — so an edit
 * in the panel actually reaches the canvas (and stays consistent with what
 * auto-bind seeds on drop). Without this, the panel wrote `config.metrics` as
 * objects for every type, which the kpi/pie renderer (reads `metricKey`), the
 * line/bar renderer (reads `metrics` as string[]), and the data-table renderer
 * (reads `columns`) all ignore or choke on.
 *
 * - `kpi`/`pie-chart` → `{ metricKey: firstKey }`
 * - `line-chart`/`bar-chart` → `{ metrics: keys (string[]) }`  (renderer reads strings)
 * - `data-table` → `{ columns: keys, metrics: objects }`  (renderer reads `columns`;
 *   the panel reads `metrics` objects via `normalizeSelectedMetrics`)
 *
 * Returns the partial config to merge.
 */
function buildMetricWritePatch(
  widgetType: string,
  selected: SelectedMetric[],
): Record<string, unknown> {
  const keys = selected.map((m) => m.key);
  if (SINGLE_METRIC_KEY_TYPES.has(widgetType)) {
    return { metricKey: keys[0] ?? "" };
  }
  if (widgetType === COLUMNS_RENDERER_TYPE) {
    return { columns: keys, metrics: selected };
  }
  // line-chart / bar-chart: the renderer reads `config.metrics` as string[].
  // For MULTI-INTEGRATION support we ALWAYS persist a sidecar map of each metric
  // key → its integrationId (config.metricIntegrations). This PINS each metric to
  // its own platform, so switching the Integration dropdown can never reassign an
  // already-picked metric to the new platform (the bug: a GA4 `sessions` metric
  // showing the Facebook logo after switching to Facebook Ads). The renderer still
  // reads the bare string[] (unchanged); the server uses the map to fetch each
  // metric from its OWN platform. Empty when no metric carries an integrationId.
  const metricIntegrations = Object.fromEntries(
    selected.filter((m) => m.integrationId).map((m) => [m.key, m.integrationId]),
  );
  // Also persist friendly labels (string[] alone loses them → the list would show
  // the raw key). Read back by normalizeSelectedMetrics.
  const metricLabels = Object.fromEntries(
    selected.filter((m) => m.label && m.label !== m.key).map((m) => [m.key, m.label]),
  );
  return {
    metrics: keys,
    metricIntegrations: Object.keys(metricIntegrations).length > 0 ? metricIntegrations : undefined,
    metricLabels: Object.keys(metricLabels).length > 0 ? metricLabels : undefined,
  };
}

/**
 * Story 37.7 review R2-1 + R3-5: read the widget's metric binding tolerating
 * BOTH the renderer/auto-bind shape and this panel's historical object shape,
 * so an auto-bound widget shows its binding here (no empty/broken selection).
 *
 * Precedence is type-aware (R3-5): a SINGLE-metric type (kpi/pie) reads its
 * canonical `config.metricKey` FIRST, so a stray `config.metrics` array can
 * never shadow the value the renderer actually uses. Other types read
 * `config.metrics` (string OR object elements), falling back to `metricKey`.
 * Junk elements (empty string / keyless object) are dropped.
 *
 * `integrationId` backfills the per-metric integrationId when a string element
 * carries none. Order is preserved; the result is always the object shape the
 * panel + `MetricSelector` expect.
 */
function normalizeSelectedMetrics(
  config: Record<string, unknown>,
  integrationId: string,
  widgetType?: string,
): SelectedMetric[] {
  const fromMetricKey = (): SelectedMetric[] => {
    const k = config.metricKey;
    return typeof k === "string" && k.length > 0
      ? [{ key: k, integrationId, label: k }]
      : [];
  };

  // Single-metric types: the canonical source is `metricKey` (R3-5).
  if (widgetType && SINGLE_METRIC_KEY_TYPES.has(widgetType)) {
    const single = fromMetricKey();
    if (single.length > 0) return single;
  }

  // Per-metric sidecars (multi-integration line/bar) + label map.
  const metricIntegrations = (config.metricIntegrations ?? {}) as Record<string, string>;
  const metricLabels = (config.metricLabels ?? {}) as Record<string, string>;
  // A metric's platform comes FROM THE METRIC, not the current dropdown. Order:
  // explicit pin (sidecar) → the metric's CANONICAL platform (METRIC_PLATFORM_MAP)
  // → the current integration only as a last resort. This is what stops a GA4
  // `sessions` metric from "becoming" Facebook when you switch the dropdown — and
  // it fixes existing widgets that have no sidecar yet (retroactive).
  const platformFor = (key: string): string => metricIntegrations[key] ?? METRIC_PLATFORM_MAP[key] ?? integrationId;

  const raw = config.metrics;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .map((m): SelectedMetric | null => {
        if (typeof m === "string") {
          return m ? { key: m, integrationId: platformFor(m), label: metricLabels[m] ?? m } : null;
        }
        if (m && typeof m === "object" && typeof (m as { key?: unknown }).key === "string") {
          const o = m as { key: string; integrationId?: string; label?: string };
          return { key: o.key, integrationId: o.integrationId ?? platformFor(o.key), label: o.label ?? metricLabels[o.key] ?? o.key };
        }
        return null;
      })
      .filter((m): m is SelectedMetric => m !== null);
  }
  return fromMetricKey();
}

// ============================================================================
// DataSourceSection Component
// ============================================================================

export function DataSourceSection({
  widget,
  onUpdate,
  clientId,
}: {
  widget: WidgetConfig;
  onUpdate: (widgetId: string, changes: Partial<WidgetConfig>) => void;
  clientId?: string;
}) {
  const config = widget.config as Record<string, unknown>;
  const integrationId = (config.integrationId as string) ?? "";
  // Story 37.7 review R2-1: the metric binding can arrive in EITHER shape — the
  // renderer/auto-bind STRING shape (`config.metrics: string[]` for line/bar,
  // `config.metricKey: string` for kpi/pie) OR this panel's historical OBJECT
  // shape (`config.metrics: {key,integrationId,label}[]`). Normalize BOTH on
  // read so an auto-bound widget reflects its binding in the panel (and the
  // user can edit it), regardless of which shape produced it.
  const metrics = normalizeSelectedMetrics(config, integrationId, widget.type);
  const dimension = config.dimension as { key: string; label: string; type: string } | undefined;

  const configRef = useRef(widget.config);
  configRef.current = widget.config;

  const { toast, showToast } = useToast();

  const handleConfigChange = useCallback(
    (key: string, value: unknown) => {
      onUpdate(widget.id, {
        config: { ...configRef.current, [key]: value },
      });
    },
    [widget.id, onUpdate],
  );

  // Merge a multi-key patch in one update (Story 37.7 R3 — metric write path).
  // A patch value of `undefined` DELETES that key (so e.g. dropping back to one
  // integration removes the metricIntegrations sidecar rather than leaving it set).
  const handleConfigPatch = useCallback(
    (patch: Record<string, unknown>) => {
      const next = { ...configRef.current, ...patch } as Record<string, unknown>;
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined) delete next[k];
      }
      onUpdate(widget.id, { config: next });
    },
    [widget.id, onUpdate],
  );

  // Fetch client's connected integrations
  const { integrations, connectedPlatforms, isLoading: integrationsLoading } = useClientIntegrations(clientId);

  // ── Story 32.6: aggregation indicator + account override picker ──────────
  // The widget's `config.integrationIds` (ROW UUIDs — never platform slugs)
  // scopes the server-side multi-account aggregate (Story 31-30). All the
  // selection algebra lives in lib/widgetAccountSelection.ts; this component
  // only renders. No new fetch: the SAME useClientIntegrations rows above are
  // the attachment source (parity with the server candidate set, which
  // excludes only `paused`).
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);
  const [overrideCapExceeded, setOverrideCapExceeded] = useState(false);

  // Review R1-1: depend on the hook's STABLE `integrations` array (useQuery
  // data identity), not `connectedPlatforms` — the hook re-creates that
  // array every render, which would defeat this whole memo chain.
  const involvedPlatforms = useMemo(
    () => deriveInvolvedPlatforms({ type: widget.type, config }, integrations.map((i) => i.type)),
    [widget.type, config, integrations],
  );
  const attachmentGroups = useMemo(
    () => groupAttachments(integrations, involvedPlatforms),
    [integrations, involvedPlatforms],
  );
  // `[]` mirrors the server's "ALL accounts" normalization → treated as no
  // override (31-30 review F4).
  const storedIntegrationIds = useMemo(
    () => normalizeStoredIntegrationIds(config.integrationIds),
    [config.integrationIds],
  );
  const checkedAccountIds = useMemo(
    () => deriveCheckedIds(attachmentGroups, storedIntegrationIds),
    [attachmentGroups, storedIntegrationIds],
  );
  const totalAttachmentCount = countAttachments(attachmentGroups);
  // K of "Using K of N": stored ids still present in the CURRENT attachment
  // set — stale ids of since-disconnected accounts never inflate it (AC #9).
  const selectedExistingCount = storedIntegrationIds
    ? countSelectedExisting(attachmentGroups, storedIntegrationIds)
    : totalAttachmentCount;
  // Visible only when account-level aggregation actually happens: some
  // involved platform has 2+ non-paused attachments. clientId undefined
  // (mock/sample editor mode) disables the hook → no groups → hidden; a
  // `resolve:` cross-tool sentinel derives to zero involved platforms.
  const showAggregationIndicator =
    Boolean(clientId) &&
    DATA_CONNECTED_WIDGET_TYPES.includes(widget.type) &&
    hasMultiAttachmentPlatform(attachmentGroups);

  // ── Story 17.4 AC #10: markup indicator (agency-eyes-only) ────────────────
  // A hint-tier status line that appears only when (a) a real client is bound
  // (not mock/sample mode), (b) ≥1 selected metric is markup-affected on an
  // involved platform, and (c) that platform has an applicable markup. All the
  // gating/copy algebra lives in lib/markupIndicator.ts (mirrors 32.6); the
  // config panel never renders in portal/share/PDF surfaces, so this string is
  // structurally invisible to clients. Hook is disabled when clientId is
  // undefined → no markup rows → indicator hidden.
  const { markups } = useClientMarkups(clientId);
  const markupIndicator = useMemo(
    () =>
      computeMarkupIndicator({
        hasClient: Boolean(clientId),
        involvedPlatforms,
        metrics: metrics.map((m) => ({ key: m.key, integrationId: m.integrationId })),
        markups,
      }),
    [clientId, involvedPlatforms, metrics, markups],
  );

  const handleAccountToggle = useCallback(
    (attachmentId: string, include: boolean) => {
      const next = new Set(checkedAccountIds);
      if (include) next.add(attachmentId);
      else next.delete(attachmentId);
      const result = computeSelectionPatch(attachmentGroups, next);
      if (result.kind === "too-many") {
        // Schema cap (max 20 ids) — do not write, degrade with a hint (AC #5e).
        setOverrideCapExceeded(true);
        return;
      }
      setOverrideCapExceeded(false);
      // `undefined` DELETES the key via handleConfigPatch — the default
      // all-attachments aggregate auto-includes future attachments (AC #5a);
      // `write` is structurally non-empty (never `[]`, 31-30 review F4).
      handleConfigPatch({
        integrationIds: result.kind === "write" ? result.integrationIds : undefined,
      });
    },
    [checkedAccountIds, attachmentGroups, handleConfigPatch],
  );

  const handleResetToAllAccounts = useCallback(() => {
    setOverrideCapExceeded(false);
    handleConfigPatch({ integrationIds: undefined });
  }, [handleConfigPatch]);

  // Fetch schema for selected integration
  const { metrics: schemaMetrics, dimensions: schemaDimensions, isLoading: schemaLoading } = useIntegrationSchema(integrationId || undefined);

  // Build integration options: connected first, then unconnected active, then coming soon
  const integrationOptions = useMemo(() => {
    const connected: Array<{ platform: string; displayName: string; domain: string; status: "connected"; accountName?: string }> = [];
    const unconnected: Array<{ platform: string; displayName: string; domain: string; status: "not-connected" }> = [];
    const comingSoon: Array<{ platform: string; displayName: string; domain: string; status: "coming-soon" }> = [];

    for (const pp of PICKER_PLATFORMS) {
      if (connectedPlatforms.includes(pp.platform)) {
        const integration = integrations.find((i) => i.type === pp.platform);
        connected.push({
          platform: pp.platform,
          displayName: pp.displayName,
          domain: pp.domain,
          status: "connected",
          accountName: integration?.accountName ?? undefined,
        });
      } else if (pp.isActive) {
        unconnected.push({
          platform: pp.platform,
          displayName: pp.displayName,
          domain: pp.domain,
          status: "not-connected",
        });
      } else {
        comingSoon.push({
          platform: pp.platform,
          displayName: pp.displayName,
          domain: pp.domain,
          status: "coming-soon",
        });
      }
    }

    return { connected, unconnected, comingSoon };
  }, [connectedPlatforms, integrations]);

  // SearchableSelect groups — the custom popover owns search/filtering, so we
  // only describe the rows (logo + label, disabled state, search keywords).
  const integrationGroups = useMemo<SearchableSelectGroup[]>(() => {
    const row = (opt: { platform: string; domain: string; displayName: string }, label: string, search: string, disabled?: boolean, title?: string) => ({
      value: opt.platform,
      searchText: search,
      disabled,
      title,
      content: (
        <span className="flex items-center gap-2">
          <img src={logoUrl(opt.domain)} alt="" className="h-5 w-5 shrink-0 rounded-sm" />
          <span className="truncate">{label}</span>
        </span>
      ),
    });
    const groups: SearchableSelectGroup[] = [];
    if (integrationOptions.connected.length > 0) {
      groups.push({
        key: "connected",
        label: "Connected",
        options: integrationOptions.connected.map((o) =>
          row(o, `${o.displayName}${o.accountName ? ` — ${o.accountName}` : ""}`, `${o.displayName} ${o.accountName ?? ""}`),
        ),
      });
    }
    if (integrationOptions.unconnected.length > 0) {
      groups.push({
        key: "unconnected",
        label: "Not Connected",
        options: integrationOptions.unconnected.map((o) =>
          row(o, o.displayName, o.displayName, true, "Connect this integration in Client Settings"),
        ),
      });
    }
    if (integrationOptions.comingSoon.length > 0) {
      groups.push({
        key: "coming-soon",
        label: "Coming Soon",
        options: integrationOptions.comingSoon.map((o) =>
          row(o, `${o.displayName} — Coming Soon`, o.displayName, true),
        ),
      });
    }
    return groups;
  }, [integrationOptions]);

  // Handle integration switch.
  // MULTI-METRIC widgets (line-chart / data-table) support metrics from DIFFERENT
  // integrations on the same chart — each metric records its own integrationId via
  // the metricIntegrations sidecar. Switching the dropdown there only changes the
  // CONTEXT for the NEXT metric you add; it keeps the metrics you already picked.
  // SINGLE-metric widgets (kpi/pie) reset on switch (one platform per metric).
  const handleIntegrationChange = useCallback(
    (newIntegrationId: string) => {
      if (MULTI_METRIC_WIDGET_TYPES.has(widget.type)) {
        onUpdate(widget.id, {
          config: { ...configRef.current, integrationId: newIntegrationId, dimension: undefined, previewData: undefined },
        });
        return;
      }

      const currentConfig = configRef.current as Record<string, unknown>;
      const previousIntegrationId = (currentConfig.integrationId as string) ?? "";
      // Normalize so the reset toast fires for ANY binding shape — including an
      // auto-bound kpi/pie that stores its metric under `metricKey` with no
      // `config.metrics` (review R3-4); the raw `metrics` read missed those.
      const currentMetrics = normalizeSelectedMetrics(currentConfig, previousIntegrationId, widget.type);

      if (previousIntegrationId && previousIntegrationId !== newIntegrationId && currentMetrics.length > 0) {
        const platformLabel = PICKER_PLATFORMS.find((p) => p.platform === newIntegrationId)?.displayName ?? newIntegrationId;
        showToast("info", `Metric selection reset — switched to ${platformLabel}`);
      }

      const next: Record<string, unknown> = {
        ...configRef.current,
        integrationId: newIntegrationId,
        // Clear EVERY binding shape (R3-4) so no stale key survives the switch
        // pointing at the old platform's metric.
        metrics: [],
        metricKey: "",
        columns: [],
        dimension: undefined,
        previewData: undefined,
      };
      // Story 32.6 AC #8: an account override of OLD-platform row UUIDs
      // surviving a platform switch would intersect to ∅ server-side and
      // render the fail-loud "no longer connected" envelope on an innocent
      // widget. DELETE the key (default all-attachments for the new platform).
      // The multi-metric path above keeps `integrationIds` untouched — its
      // switch is a context-only change that keeps the metrics.
      delete next.integrationIds;
      onUpdate(widget.id, { config: next });
    },
    [widget.id, widget.type, onUpdate, showToast],
  );

  // Check if current integration has a shared time dimension (AC #5, Task 2.6)
  const hasTimeDimension = schemaDimensions.some((d) => d.type === "Time");

  // Handle metric selection. Writes the RENDERER-correct shape per type (R3) so
  // an edit reaches the canvas, mirroring what auto-bind seeds on drop.
  const handleMetricSelect = useCallback(
    (metricKey: string, metricLabel: string) => {
      const currentConfig = configRef.current as Record<string, unknown>;
      // Normalize first so we never dedup/append against an auto-bound STRING
      // shape with `undefined` keys (review R2-1).
      const currentMetrics = normalizeSelectedMetrics(currentConfig, integrationId, widget.type);

      let updated: SelectedMetric[];
      if (MULTI_METRIC_WIDGET_TYPES.has(widget.type)) {
        // Multi-metric: add to list (dedup by key+integration).
        const alreadySelected = currentMetrics.some((m) => m.key === metricKey && m.integrationId === integrationId);
        if (alreadySelected) return;

        // Validate shared time dimension when mixing integrations (AC #5)
        if (!hasTimeDimension && currentMetrics.length > 0) {
          const platformLabel = PICKER_PLATFORMS.find((p) => p.platform === integrationId)?.displayName ?? integrationId;
          showToast("info", `${platformLabel} does not support time-series — metrics may not align on a shared time axis`);
        }
        updated = [...currentMetrics, { key: metricKey, integrationId, label: metricLabel }];
      } else {
        // Single-metric: replace.
        updated = [{ key: metricKey, integrationId, label: metricLabel }];
      }
      const patch = buildMetricWritePatch(widget.type, updated);

      // Story 32.6 AC #6c — reconcile the account override on metric add: when
      // `integrationIds` is present and the new metric's platform (the current
      // dropdown — that is what the sidecar pins it to) contributes NO id to
      // the list, the server would silently drop that platform on cross-
      // platform paths. Auto-append ALL of its non-paused attachment ids
      // (default-all semantics for the newcomer). If the union would exceed
      // the 20-id schema cap, drop the override entirely (default-all also
      // includes the new platform). Metric REMOVAL needs no reconciliation —
      // ids of no-longer-consulted platforms are inert server-side.
      if (MULTI_METRIC_WIDGET_TYPES.has(widget.type) && integrationId && !isCrossToolResolveSentinel(integrationId)) {
        const storedIds = normalizeStoredIntegrationIds(currentConfig.integrationIds);
        if (storedIds) {
          const reconciled = reconcileOnMetricAdd(storedIds, integrationId, integrations);
          if (reconciled !== storedIds) patch.integrationIds = reconciled;
        }
      }
      handleConfigPatch(patch);
    },
    [widget.type, integrationId, integrations, handleConfigPatch, hasTimeDimension, showToast],
  );

  // Handle metric removal (multi-metric widgets) — re-derives the
  // renderer-correct shape from the normalized list (R3).
  const handleMetricRemove = useCallback(
    (index: number) => {
      const currentConfig = configRef.current as Record<string, unknown>;
      const currentMetrics = normalizeSelectedMetrics(currentConfig, integrationId, widget.type);
      const updated = currentMetrics.filter((_, i) => i !== index);
      handleConfigPatch(buildMetricWritePatch(widget.type, updated));
    },
    [widget.type, integrationId, handleConfigPatch],
  );

  // Handle dimension selection
  const handleDimensionSelect = useCallback(
    (dimensionKey: string, dimensionLabel: string, dimensionType: string) => {
      handleConfigChange("dimension", { key: dimensionKey, label: dimensionLabel, type: dimensionType });
    },
    [handleConfigChange],
  );

  return (
    // Group header matches the Appearance groups (these fields previously had no
    // title); fields stay label-left + 130px under it.
    <div data-testid="data-source-section" className="space-y-3">
      <label className="block text-[13.5px] font-medium text-foreground">Integration &amp; metrics</label>
      {/* Integration — label-left + 130px control, matching the Appearance fields. */}
      <div className="grid grid-cols-[1fr_130px] items-center gap-2">
        <label className="min-w-0 text-[13px] text-foreground">Integration</label>
        {integrationsLoading ? (
          <div className="flex h-9 w-full items-center rounded-lg border-[0.5px] border-input bg-background px-3 text-[13.5px] text-muted-foreground shadow-xs">…</div>
        ) : (
          <SearchableSelect
            value={integrationId || undefined}
            onChange={(v) => handleIntegrationChange(v)}
            groups={integrationGroups}
            placeholder="Select…"
            searchPlaceholder="Search integrations..."
            emptyText="No integrations found"
            testId="integration-select"
            searchTestId="integration-search"
            triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
          />
        )}
      </div>

      {/* Aggregation indicator (Story 32.6) — a status line, full-row like the
          MetricSelector deprecation notice (not the 130px control column).
          Visible only when some involved platform has 2+ non-paused
          attachments for this client. Copy uses the platform-neutral noun
          "accounts" (GA4 properties, Meta ad accounts, LinkedIn pages all
          render through this one component). */}
      {showAggregationIndicator && (
        <div data-testid="aggregation-indicator" className="text-[11px] text-muted-foreground">
          {storedIntegrationIds
            ? `Using ${selectedExistingCount} of ${totalAttachmentCount} accounts`
            : `Aggregating across ${totalAttachmentCount} accounts`}
          {" — "}
          <button
            type="button"
            onClick={() => {
              // Review R1-3: the cap hint describes the LAST toggle attempt —
              // it must not survive a collapse/reopen cycle.
              setOverrideCapExceeded(false);
              setAccountPickerOpen((open) => !open);
            }}
            aria-expanded={accountPickerOpen}
            // ARIA: aria-controls must reference an element that exists in the
            // DOM — the picker is unmounted while collapsed (AnimatePresence),
            // so the attribute is only set while open.
            aria-controls={accountPickerOpen ? "aggregation-override-picker" : undefined}
            className="inline-flex items-center gap-0.5 align-bottom text-primary cursor-pointer hover:underline"
          >
            change?
            {accountPickerOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
          </button>
        </div>
      )}

      {/* Markup indicator (Story 17.4 AC #10) — agency-eyes-only hint-tier
          status line, stacked BELOW the aggregation indicator. Same register
          as the 32.6 precedent: full-row text-[11px] text-muted-foreground.
          Hidden unless all three gates pass (see computeMarkupIndicator). */}
      {markupIndicator && (
        <div
          data-testid="markup-indicator"
          className="flex items-center gap-1 text-[11px] text-muted-foreground"
        >
          <Percent size={14} className="shrink-0" />
          <span>{markupIndicator.copy}</span>
        </div>
      )}

      {/* Account override picker (Story 32.6) — inline expandable inside the
          data-source group (§10 dense-editor register: no Dialog, no Sheet,
          no popover). Rows write `integrations` ROW UUIDs — never platform
          slugs (Trap #1). */}
      <AnimatePresence initial={false}>
        {showAggregationIndicator && accountPickerOpen && (
          <motion.div
            key="aggregation-override-picker"
            id="aggregation-override-picker"
            data-testid="aggregation-override-picker"
            role="group"
            aria-label="Accounts included in this widget"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="space-y-1 px-0.5">
              {attachmentGroups.map((group) => {
                const pinned = group.attachments.length === 1;
                const platformMeta = PICKER_PLATFORMS.find((p) => p.platform === group.platform);
                return (
                  <div key={group.platform}>
                    {/* Platform group label — only when the widget spans platforms. */}
                    {attachmentGroups.length > 1 && (
                      <div className="text-[11px] font-medium text-muted-foreground">
                        {platformMeta?.displayName ?? group.platform}
                      </div>
                    )}
                    {group.attachments.map((attachment) => {
                      const accountLabel = attachment.accountName ?? "Unnamed account";
                      const checked = pinned || checkedAccountIds.has(attachment.id);
                      // Min-1 per multi-attachment platform (AC #7): the last
                      // checked Switch cannot be turned off — unchecking
                      // everything would produce the server's fail-loud
                      // empty-intersection envelope (or a silent drop on
                      // cross-platform paths).
                      const lastChecked = !pinned && isLastCheckedInGroup(group, checkedAccountIds, attachment.id);
                      return (
                        <div
                          key={attachment.id}
                          data-testid="aggregation-account-row"
                          className="flex h-9 items-center gap-2"
                        >
                          {platformMeta ? (
                            <img
                              src={logoUrl(platformMeta.domain)}
                              alt=""
                              className="h-4 w-4 shrink-0 rounded-sm"
                              title={platformMeta.displayName}
                            />
                          ) : (
                            <span className="h-4 w-4 shrink-0 rounded-sm bg-muted" title={group.platform} />
                          )}
                          <span className="min-w-0 flex-1 truncate text-[12.5px] text-foreground" title={accountLabel}>
                            {accountLabel}
                          </span>
                          {/* Single-attachment involved platforms are pinned:
                              always in the written list, so a cross-platform
                              widget never silently drops them (AC #6b). */}
                          {pinned && (
                            <span className="shrink-0 text-[11px] text-muted-foreground">Always included</span>
                          )}
                          <Switch
                            aria-label={`Include ${accountLabel}`}
                            // Report-editor navy, not the alert-red theme
                            // primary (the KPI Trend-line Switch precedent).
                            className="data-[state=checked]:bg-[#1e2a44]"
                            checked={checked}
                            disabled={pinned || lastChecked}
                            title={
                              pinned
                                ? "Always included"
                                : lastChecked
                                  ? "At least one account must be included"
                                  : undefined
                            }
                            onCheckedChange={(include) => handleAccountToggle(attachment.id, include)}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              {/* >20-id schema cap — degrade with a hint, never write (AC #5e). */}
              {overrideCapExceeded && (
                <div data-testid="aggregation-cap-hint" className="text-[11px] text-muted-foreground">
                  Too many accounts to override — using all
                </div>
              )}
              {/* Quiet reset — deletes the key (default all-attachments). */}
              {storedIntegrationIds && (
                <button
                  type="button"
                  data-testid="aggregation-reset"
                  onClick={handleResetToAllAccounts}
                  className="text-[11px] text-primary cursor-pointer hover:underline"
                >
                  Reset to all accounts
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Metric Selector — shown when integration is selected */}
      {integrationId && (
        <MetricSelector
          widgetType={widget.type}
          metrics={schemaMetrics}
          selectedMetrics={metrics}
          isLoading={schemaLoading}
          isMultiMetric={MULTI_METRIC_WIDGET_TYPES.has(widget.type)}
          onSelect={handleMetricSelect}
          compatibility={WIDGET_METRIC_COMPATIBILITY[widget.type] ?? ["*"]}
        />
      )}

      {/* Dimension Selector — shown for supported widget types when integration is selected */}
      {integrationId && DIMENSION_WIDGET_TYPES.has(widget.type) && (
        <DimensionSelector
          dimensions={schemaDimensions}
          selectedDimension={dimension}
          isLoading={schemaLoading}
          onSelect={handleDimensionSelect}
        />
      )}

      {/* Selected metrics — multi-metric widgets. Compact rows aligned with the
          rest of the panel (h-9, 0.5px border, 13.5px text); real integration logo
          + the metric label + a quiet remove button. */}
      {MULTI_METRIC_WIDGET_TYPES.has(widget.type) && metrics.length > 0 && (
        <div data-testid="selected-metrics-list" className="space-y-1.5">
          <div className="space-y-1.5">
            {metrics.map((m, i) => {
              const platform = PICKER_PLATFORMS.find((p) => p.platform === m.integrationId);
              return (
                <div
                  key={`${m.integrationId}-${m.key}-${i}`}
                  className="flex h-9 items-center gap-2 px-0.5"
                >
                  {platform ? (
                    <img src={logoUrl(platform.domain)} alt="" className="h-4 w-4 shrink-0 rounded-sm" title={platform.displayName} />
                  ) : (
                    <span className="inline-flex h-4 shrink-0 items-center rounded px-1 text-[9px] font-medium text-muted-foreground" title={m.integrationId}>
                      {PLATFORM_SHORT_LABELS[m.integrationId] ?? m.integrationId}
                    </span>
                  )}
                  {/* Label only as wide as its text, so the remove X sits right next
                      to it (not pushed to the far right). */}
                  <span className="min-w-0 max-w-[180px] truncate text-[12.5px] text-foreground" title={m.label}>{m.label}</span>
                  <button
                    type="button"
                    onClick={() => handleMetricRemove(i)}
                    aria-label={`Remove ${m.label}`}
                    title="Remove metric"
                    className="inline-flex touch-target-sm shrink-0 text-muted-foreground hover:text-destructive cursor-pointer"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
